/**
 * HUD (DESIGN-client §7): top: hand N/4 and the phase timer. Bottom-right: time bank (+15s,
 * charges), "?" rules, gear settings, emote button. `pointer-events:none` except controls.
 *
 * ## The score is not here any more
 *
 * It is on the two `TeamPanel` plaques the scene draws on the felt, and that is the only place it
 * lives. The DOM carried US/THEM cards with the total and ten bag dots each, which made the score
 * two things in two idioms: a walnut plaque on the table and a pair of chrome cards floating over
 * it, disagreeing about which one a player should read. On a 390x844 phone they also cost more than
 * they were worth — they sat in the top band, and the scene's "How many books will you win?" prompt
 * is drawn in that band, so a first-time player read "na … n? Drag" and was asked to bid on it.
 *
 * What is left up here is the one thing the felt does not say: which hand of the match this is, and
 * how long is left on the clock.
 *
 * The `--hud-*` insets this positions against are set on `.table-screen` by `TableScreen`, not
 * here. They are the scene's measured clearance and every overlay needs them: while they were set
 * on this element, `Subtitle`, `BlindNilPrompt` and `Spectator` were siblings of it and could not
 * inherit them, so all three silently fell back to the raw safe area and an announcer bark sat on
 * top of the card fan in portrait.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { serverNow } from '../../net/wire';
import { store, useStore } from '../../store/store';
import { handsTotal, timerFraction } from '../helpers';
import { rulesFromJson } from '../tableOptions';
import { intents } from '../runtime';
import { RulesSheet } from '../sheets/Rules';
import { SettingsSheet } from '../sheets/Settings';
import { Button } from '../ui';
import { EmotePicker } from './EmotePicker';

type SheetName = 'rules' | 'settings' | null;

/**
 * Off screen but still in the accessibility tree, as an inline style rather than a class.
 *
 * `theme.css` belongs to another pass and has no utility for this; one object here is cheaper than
 * a shared class nobody else asked for.
 */
const HIDDEN_CONTROL: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

// --- server time (review #15) -------------------------------------------------------------
// `timer.endsAt` and `timer.startedAt` are the SERVER's clock. Ticking on `Date.now()` meant a
// phone 45 seconds fast drew every bar as already drained and showed `0s` for the player's whole
// turn. `serverNow()` is the driver's estimate of the server's clock, and falls back to
// `Date.now()` when there is no driver (the scripted table), which is exactly right there.
function useNow(active: boolean, every = 250): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => setNow(serverNow()), every);
    return () => clearInterval(iv);
  }, [active, every]);
  return now;
}
// --- end server time ----------------------------------------------------------------------

export function Hud() {
  const table = useStore((s) => s.table);
  const seat = useStore((s) => s.session.seat);
  /*
   * The bid set piece owns the top of the screen while this is set, so the top row stands down.
   *
   * `BidLayer` used to draw its own prompt in this very band — above a rail that ran 45% of the way
   * up the viewport — so on a phone the two landed on top of each other and the sentence read
   * "na … n? Drag": the one decision the player is being asked for, illegible, behind a score they
   * can recall. The slider is horizontal now and its copy sits low on the felt, but the row still
   * stands down, for the reason below.
   *
   * This used to be `hidden={bidLive}` on the element, which did nothing at all. `[hidden]` is a
   * user-agent rule at specificity 0-0-1 and `.hud__top { display: flex }` outranks it, so both HUD
   * rows have been painted through every bid since the attribute was added. Conditional rendering
   * cannot be lost to a stylesheet.
   *
   * The bottom cluster goes too, which it did not used to. The argument for keeping it was the
   * +15s time bank, and that argument is gone: `TableRoom.spendBankFor` auto-spends a bank when the
   * bid clock expires (holding one back, `BID_BANK_RESERVE`), so pressing the button by hand is
   * doing what the server was about to do anyway. It was decoration in the phase it was kept for. A
   * playtester asked for the bid screen to be the bid and nothing else, and four buttons at the
   * foot of the felt are four things to look at instead of the slider. Everything here comes
   * straight back the moment the bid locks.
   */
  const bidLive = useStore((s) => s.presentation.bidLive);
  /*
   * The emote picker is store state, not local state, because the thing that opens it is the
   * player's own avatar on the felt — a Pixi object, which cannot reach React. The scene writes
   * `presentation.emotePickerOpen` through `TablePorts.openEmotes`; this reads it.
   */
  const emotePickerOpen = useStore((s) => s.presentation.emotePickerOpen);
  const openEmotes = useCallback(() => store.getState().setPresentation({ emotePickerOpen: true }), []);
  const closeEmotes = useCallback(() => store.getState().setPresentation({ emotePickerOpen: false }), []);
  const [sheet, setSheet] = useState<SheetName>(null);
  const timer = table?.timer;
  const timerActive = !!timer && timer.kind !== 'none' && timer.endsAt > 0;
  const now = useNow(timerActive);

  const total = handsTotal(table);
  const mySeat = seat != null ? table?.seats[seat] : undefined;
  const banks = mySeat?.timeBanksLeft ?? 0;
  const myTimer = timerActive && (timer.seat === seat || timer.seat === -1 || timer.kind === 'bid');
  // `now` is server time, so this subtraction is server-minus-server whatever the device clock says.
  const remaining = timerActive ? Math.max(0, timer.endsAt - now) : 0;
  // The window the server actually armed, which is also the one a time bank lengthened. The rules
  // are the fallback for a server too old to send it (and for the scripted driver): hard-coded
  // 20s/10s meant a Blitz table (15s/7s) opened its bar three-quarters full and never filled, and
  // the 3s forced-play timer opened at 30%.
  const rules = useMemo(() => rulesFromJson(table?.rulesJson ?? ''), [table?.rulesJson]);
  const fullMs = timer && timer.durationMs > 0 ? timer.durationMs : timer?.kind === 'bid' ? rules.timers.bidMs : rules.timers.playMs;
  const frac = timerActive ? timerFraction(remaining, fullMs) : 0;

  return (
    <div className="hud" data-testid="hud">
      {/*
        One row along the top: what hand this is, then the two ways out of the table.
        
        The "?" and the gear used to sit at the foot of the screen, stacked under the +15s, which
        put three unrelated things in the corner the player's own cards reach into. They are chrome
        — the way out of the game rather than part of it — so they belong in the same band as the
        hand counter, which is the other thing on screen that is about the match rather than about
        the trick. Counter first, then the buttons: the counter is content and the buttons are the
        frame, so the frame sits at the extreme corner.
      */}
      {!bidLive && (
        <div className="hud__top">
          <div className="hud__hand" data-testid="hand-no">
            {table ? (
              <>
                Hand {Math.max(1, table.handNo)}
                {total ? `/${total}` : ''}
                {table.suddenDeath > 0 ? ' · sudden death' : ''}
              </>
            ) : (
              '—'
            )}
            {timerActive && (
              <div className="timer-bar" aria-label={`${Math.ceil(remaining / 1000)} seconds`}>
                <div className={`timer-bar__fill${remaining < 5000 ? ' timer-bar__fill--hot' : ''}`} style={{ width: `${frac * 100}%` }} />
              </div>
            )}
          </div>
          <div className="hud__controls">
            <Button icon aria-label="How to play" testId="hud-rules" onClick={() => setSheet('rules')}>
              ?
            </Button>
            <Button icon aria-label="Settings" testId="hud-settings" onClick={() => setSheet('settings')}>
              ⚙
            </Button>
          </div>
        </div>
      )}

      {/*
        The time bank alone, in the bottom-right corner.
        
        It is the one control that is about the moment rather than about the match — you press it
        because this turn is running out — so it stays near the felt while the rest of the chrome
        went to the top. The corner is where it can be biggest without being in the way, and
        `--hud-corner` is the solver's answer to whether that corner is actually free: it is the
        safe area's own bottom wherever the card fan does not reach the right-hand edge, and the
        fan's top otherwise (portrait, where the fan spans the whole width).

        Focus mode: it stands down for the bid, like the top row. `TableRoom.spendBankFor` auto-
        spends a bank when the bid clock expires, so pressing it by hand during a bid does what the
        server was about to do anyway.
      */}
      {!bidLive && seat != null && (
        <div className="hud__bottom">
          <span className="timebank">
            <Button
              testId="time-bank"
              aria-label={`Use time bank, ${banks} left`}
              disabled={banks <= 0 || !myTimer}
              onClick={() => intents()?.timeBank()}
            >
              +15s
            </Button>
            <span className="timebank__pips" aria-hidden>
              {[0, 1].map((i) => (
                <span key={i} className={`timebank__pip${i < banks ? '' : ' timebank__pip--spent'}`} />
              ))}
            </span>
          </span>
        </div>
      )}

      {/*
        The 💬 button is gone. Sending a sticker is something you do AS a player, and the
        affordance for it is now the player: tapping your own avatar on the felt opens the picker,
        with a speech-bubble mark on its shoulder where the bid chip used to be
        (`scene/objects/SeatView`). Three buttons in a corner, one of which was a face you had to
        learn stood for you, is what that replaced.

        This is what remains: a control with no box, off screen, for the e2e specs and for anyone
        driving the shell with a keyboard or a screen reader — the Pixi avatar is a canvas and has
        neither a tab stop nor a name.

        Outside the focus-mode guard on purpose. The visible cluster stands down for a bid; this
        does not, because it is the ONLY way a keyboard or screen-reader player reaches the picker,
        and "you may not emote while bidding" is a rule nobody wrote and only they would feel.
      */}
      <button
        type="button"
        style={HIDDEN_CONTROL}
        data-testid="hud-emotes"
        aria-label="Emotes"
        disabled={seat == null}
        onClick={openEmotes}
      >
        Emotes
      </button>

      <RulesSheet open={sheet === 'rules'} onClose={() => setSheet(null)} />
      <SettingsSheet open={sheet === 'settings'} onClose={() => setSheet(null)} />
      <EmotePicker open={emotePickerOpen} onClose={closeEmotes} />
    </div>
  );
}
